import { Component, OnInit } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { ContextService, LayoutInterface, OptionDetail, RoleInterface, ServerServiceConfig } from '../context.service';
import { FormlyFieldConfig, FormlyFormOptions } from '@ngx-formly/core';
import { BehaviorSubject } from 'rxjs';

export interface LoginInfo {
  user: string;
  pwd: string;
  message: string;
}

@Component({
  selector: 'mutara-login',
  templateUrl: './login.component.html',
  standalone: false
})

export class LoginComponent implements OnInit {

  form: UntypedFormGroup = new UntypedFormGroup({});
  model: any = {};
  options: FormlyFormOptions = {
    formState: {
      hidePassword: true,
    },
  };
  wasInvalid: boolean = false;
  isVertical: boolean = false;
  layout: LayoutInterface | undefined;
  isResetting = false;
  activity: string = 'Sign In';
  passwordRegEx: RegExp = /(?=.{8,})((?=.*\d)(?=.*[a-z])(?=.*[A-Z])|(?=.*\d)(?=.*[a-zA-Z])(?=.*[\W_])|(?=.*[a-z])(?=.*[A-Z])(?=.*[\W_])).*/;
  passwordHint: string = 'Password rules: minmum 8 characters, at least 3 of the following; lowercase, uppercase, numbers, symbols';

  private roleOptionBS = new BehaviorSubject<any[]>([]);
  public roleOptions = this.roleOptionBS.asObservable();

  fields: FormlyFieldConfig[] = [
    {
      fieldGroupClassName: 'display-flex',
      fieldGroup: [
        {
          key: 'user', type: 'input',
          className: 'flex-1',
          modelOptions: {
            debounce: { default: 300 },
          },
          props: {
            label: 'Email',
            placeholder: 'me@email.com',
            required: true,
          }
        },
        {
          key: 'pwd', type: 'input',
          className: 'flex-1',
          expressions: {
            "props.label": (field: FormlyFieldConfig) => (field.options?.formState.hidePassword ? 'Password' : ' Choose New Password')
          },
          props: {
            type: 'password',
            hidePassword: true,
            placeholder: 'bubbl3Gum!',
            required: true,
            // addonRight: {
            //   icon: 'visibility_off',
            //   ariaLabel: 'Show password',
            //   onClick: (props, field, $event) => {
            //     props.hidePassword = !props.hidePassword;
            //     props.type = props.hidePassword ? 'password' : 'text';
            //     props.addonRight.ariaLabel = props.hidePassword ? 'Show password' : 'Mask password';
            //     props.addonRight.icon = props.hidePassword ? 'visibility_off' : 'visibility';
            //   }
            // },
          },
        },
        {
          key: 'forgotten', type: 'input',
          className: 'flex-1',
          expressions: {
            'hide': (field: FormlyFieldConfig) => field.options?.formState.hidePassword
          },
          validators: {
            matches: {
              expression: (c: AbstractControl) => (c.value == c.root.value?.pwd),
              message: () => 'Passwords do not match'
            },
            patterns: {
              expression: (c: AbstractControl) => this.passwordRegEx.test(c.value),
              message: () => this.passwordHint 
            }
          },
          props: {
            type: 'password',
            label: 'Confirm New Password',
            required: true,
            hidePassword: true,
            ariaLabel: 'Show password',
            // addonRight: {
            //   icon: 'visibility_off',
            //   onClick: (props, field, $event) => {
            //     props.hidePassword = !props.hidePassword;
            //     props.type = props.hidePassword ? 'password' : 'text';
            //     props.addonRight.ariaLabel = props.hidePassword ? 'Show password' : 'Mask password';
            //     props.addonRight.icon = props.hidePassword ? 'visibility_off' : 'visibility';
            //   }
            // },
          },
        },
        {
          key: 'layout', type: 'select',
          hide: true,
          className: 'flex-2',
          expressions: {
            hide: (field: FormlyFieldConfig) => {
              let hideField = !this.model.admin || this.isVertical;
              return hideField || !field.options?.formState.hidePassword;
            }
          },
          defaultValue: 'Apply to ${institution}',
          props: {
            label: 'Activity',
            options: this.roleOptions,
          }
        },]
    },
    {
      key: 'descriptive',
      type: 'alert',
      expressions: {
        hide: (field: FormlyFieldConfig) => {
          return !(!!this.isResetting || this.wasInvalid || (!this.options.formState.hidePassword && this.form.valid))
        },
        'props.statusType': (field: FormlyFieldConfig) => {
          let type;
          if (field.props) {
            if (!!this.wasInvalid) {
              type = 'invalid';
              field.props.description = 'Invalid User/Password combination. Please re-enter your password or press **Cancel** and register for an account.';
            } else if (!this.options.formState.hidePassword && this.form.valid) {
              type = 'info';
              field.props.secondaryText = '**Watch for an email or text message to verify the password change.**';
            } else if (!!this.isResetting) {
              type = 'neutral';
              field.props.description = 'After submitting the password change request, please check your email and follow the link to activate the new password. The link will only be available for 24 hours.';
            }
          }
          return type;
        },
      },
    },
    {
      key: 'buttonList', wrappers: ['button-row'],
      props: {
        align: 'end',
      },
      fieldGroup: [
        {
          key: 'forgot', type: 'button',
          expressions: {
            'props.disabled': () => (!!this.isResetting || ('' == (this.model.user ?? ''))),
            hide: () => !!this.isResetting
          },
          props: {
            type: 'button',
            label: 'Forgot Password',
            color: 'accent',
            onClick: () => {
              if ((!this.isResetting && ('' != (this.model.user ?? '')))) {
                this.isResetting = true;
                this.wasInvalid = false;
                this.options.formState.hidePassword = !this.options.formState.hidePassword;
                if (!this.options.formState.hidePassword) {
                  //this.model.forgotten = null;
                  this.form.patchValue({ forgotten: null });
                  this.activity = 'Reset Password';
                } else {
                  this.activity = 'Sign In';
                }
              }
            }
          }
        },
        {
          key: 'cancel', type: 'button',
          props: {
            type: 'button',
            label: 'Cancel',
            color: 'accent',
            onClick: () => {
              this.isResetting = false;
              this.wasInvalid = false;
              this.close();
            }
          }
        },
        {
          key: 'login', type: 'button',
          expressions: {
            'props.disabled': (field: FormlyFieldConfig) => !!this.form.invalid,
            'props.label': (field: FormlyFieldConfig) => field.options?.formState.hidePassword ? 'Login' : 'Request Password Change',
          },
          props: {
            type: 'submit',
            label: 'Login',
            color: 'accent'

          }
        }
      ]
    },
  ];
  /**
   * Class implementation
   * @param router 
   * @param contextSettings 
   * @param elementRef 
   * @param dialogRef 
   */
  constructor(
    private router: Router,
    private contextSettings: ContextService,
    private dialogRef: MatDialogRef<LoginComponent>
  ) { 
    this.contextSettings.subscribeTo("orientation", (isVertical : boolean ) => {
      this.isVertical = isVertical;
    });
    this.contextSettings.subscribeTo("applicationInfo", (config: ServerServiceConfig) => {
      if ( config.passwordRegEx ) {
        this.passwordRegEx = new RegExp( config.passwordRegEx );
      }
      if ( config.passwordHint ) {
        this.passwordHint = config.passwordHint;
      }

      let roleArray : OptionDetail[] | undefined = config.lookups?.Roles?.sort((a, b) => a.label.localeCompare(b.label)).map((element : any) => {
        let values = element.value.split('|');
        return { id: element.id, category: '', value: values[0], label: element.label, icon: values.length > 1 ? values[1] : null };
      });
      this.roleOptionBS.next( <any[]>roleArray );

    });
    this.contextSettings.subscribeTo("layoutIdentity", (layout: LayoutInterface) => {
      this.layout = layout;
    });
    this.contextSettings.subscribeTo("logout", (clearSession: boolean) => {
      if ( clearSession ) { this.model.admin = 0; }
    });
  }

  ngOnInit() {
    let userfld : FormlyFieldConfig | undefined = this.fields[0]?.fieldGroup?.[0];
    if ( userfld?.props ) {
      userfld.props.change = (field, $event ) => {
        if ( field.formControl ) {
          this.contextSettings.isAdminEmail(field.formControl.value);
        }
      };
    }

    this.contextSettings.subscribeTo("failedLogin", (failed: boolean) => {
      this.wasInvalid = failed;
    });

    // Only track if the result is an admin role or not, use is later
    this.contextSettings.subscribeTo("adminEmail", (result: RoleInterface) => {
      if (!!result) {
        this.model.admin = result.role > 0;
        if (!!result.error) {
          this.contextSettings.changeMessage(result.error);
        }
      } else {
        this.model.admin = 0;
      }
    });
  }

  save() {
    this.model.defaultLayout = this.layout;
    this.dialogRef.close({ ...this.model });
    this.form.reset();
  }

  close() {
    this.form.reset();
    this.dialogRef.close();
  }
}
